import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  FolderOpen,
  Search,
  Trash2,
  DollarSign,
  ChevronDown,
  FileSpreadsheet,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

import { useProjects } from "../hooks/useProjects";
import { useAuth } from "../hooks/useAuth";
import { formatCurrency } from "../lib/calculations";
import { ImportProjectsModal } from "../components/ImportProjectsModal";
import { ProjectImageCarousel } from "../components/ProjectImageCarousel";
import { fetchAllProjectImageUrls } from "../lib/projectImages";
 
import { supabase as _supabase } from "../lib/supabase";
import { useToast } from "../components/ui/ToastModal";
import { SkeletonLoader } from "../components/ui/SkeletonLoader";
import { useTourManager } from "../hooks/useTourManager";
import { TourEngine } from "../components/onboarding/TourEngine";
import { logError } from "../lib/logError";
import { ProjectDeleteRefusedError } from "../lib/projectDelete";



export function ProjectsPage() {
  const { projects, loading, deleteProject, refreshProjects } = useProjects();
  // Same rule as the claims_projects DELETE policy: org admin or super_admin.
  const { isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isNewProjectMenuOpen, setIsNewProjectMenuOpen] = useState(false);
  const [projectImageUrls, setProjectImageUrls] = useState<Record<string, string[]>>({});

  // Fetch all image URLs for slideshow when projects are loaded
  useEffect(() => {
    if (projects.length === 0) return;
    const projectsWithImages = projects.filter((p) => (p.imageCount || 0) > 0);
    if (projectsWithImages.length === 0) return;

    const fetchAll = async () => {
      const result: Record<string, string[]> = {};
      await Promise.all(
        projectsWithImages.map(async (p) => {
          try {
            const urls = await fetchAllProjectImageUrls(p.id);
            result[p.id] = urls;
          } catch {
            result[p.id] = p.coverImageUrl ? [p.coverImageUrl] : [];
          }
        })
      );
      setProjectImageUrls(result);
    };
    fetchAll();
  }, [projects]);

   
  const { addToast } = useToast();

  // Tour system
  const tour = useTourManager('projects');

  // Status color map (shared across badge + card border)
  const STATUS_COLORS: Record<string, { label: string; bg: string; color: string; border: string }> = {
    active: {
      label: "Active",
      bg: "rgba(16, 185, 129, 0.15)",
      color: "#10b981",
      border: "#10b981",
    },
    upcoming: {
      label: "Upcoming",
      bg: "rgba(139, 92, 246, 0.15)",
      color: "#8b5cf6",
      border: "#8b5cf6",
    },
    completed: {
      label: "Completed",
      bg: "rgba(59, 130, 246, 0.15)",
      color: "#3b82f6",
      border: "#3b82f6",
    },
    on_hold: {
      label: "On Hold",
      bg: "rgba(245, 158, 11, 0.15)",
      color: "#f59e0b",
      border: "#f59e0b",
    },
  };

  // Status badge helper
  const getStatusBadge = (status: string | null | undefined) => {
    const s = status || "active";
    const config = STATUS_COLORS[s] || { label: s, bg: "var(--bg-tertiary)", color: "var(--text-muted)", border: "var(--border-color)" };

    return (
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 12,
          background: config.bg,
          color: config.color,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {config.label}
      </span>
    );
  };



  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.job_location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.job_address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || (project.status || "active") === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setDeleteConfirm(null);
    } catch (error) {
      logError("Failed to delete project:", error);
      setDeleteConfirm(null);
      addToast({
        type: "error",
        title: "Project not deleted",
        message:
          error instanceof ProjectDeleteRefusedError
            ? error.message
            : "Nothing was removed. Try again or ask an admin.",
      });
    }
  };

  if (loading) {
    return <SkeletonLoader variant="projects-grid" count={6} />;
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--spacing-xl)",
          flexWrap: "wrap",
          gap: "var(--spacing-md)",
        }}
      >
        <div>
          <h1 style={{ marginBottom: "var(--spacing-xs)" }}>Projects</h1>
          <p style={{ color: "var(--text-secondary)" }}>
            {projects.length} project{projects.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            alignItems: "center",
          }}
        >
          {/* New Project Button */}
          <div style={{ position: "relative" }}>
            <button
              className="btn btn-primary"
              onClick={() => setIsNewProjectMenuOpen(!isNewProjectMenuOpen)}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Plus size={18} />
              New Project
              <ChevronDown size={14} style={{ opacity: 0.7 }} />
            </button>

            {isNewProjectMenuOpen && (
              <>
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 40,
                  }}
                  onClick={() => setIsNewProjectMenuOpen(false)}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 4,
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--border-radius-md)",
                    boxShadow: "var(--shadow-lg)",
                    padding: 4,
                    minWidth: 180,
                    zIndex: 50,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Link
                    to="/projects/new"
                    className="btn btn-ghost"
                    onClick={() => setIsNewProjectMenuOpen(false)}
                    style={{
                      justifyContent: "flex-start",
                      gap: 8,
                      padding: "8px 12px",
                      textDecoration: "none",
                    }}
                  >
                    <Plus size={16} style={{ opacity: 0.7 }} />
                    Create Manually
                  </Link>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setIsNewProjectMenuOpen(false);
                      setIsImportModalOpen(true);
                    }}
                    style={{
                      justifyContent: "flex-start",
                      gap: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <FileSpreadsheet size={16} style={{ opacity: 0.7 }} />
                    Import from CSV
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ImportProjectsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={refreshProjects}
      />

      {/* Search and Filter */}
      <div
        style={{
          marginBottom: "var(--spacing-lg)",
          display: "flex",
          gap: "var(--spacing-md)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", flex: "1 1 250px", maxWidth: 400 }}>
          <Search
            size={18}
            style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
            }}
          />
          <input
            type="text"
            className="form-input"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 40 }}
          />
        </div>
        <select
          className="form-input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto", minWidth: 130, flex: "0 0 auto" }}
          data-tour-id="projects-status-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On Hold</option>
        </select>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div
          className="card"
          style={{
            textAlign: "center",
            padding: "var(--spacing-2xl)",
          }}
        >
          <FolderOpen
            size={64}
            style={{ marginBottom: "var(--spacing-lg)", opacity: 0.3 }}
          />
          <h3 style={{ marginBottom: "var(--spacing-sm)" }}>
            {searchQuery ? "No projects found" : "No projects yet"}
          </h3>
          <p
            style={{
              color: "var(--text-muted)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            {searchQuery
              ? "Try adjusting your search query"
              : "Create your first project to start managing claims"}
          </p>
          {!searchQuery && (
            <Link to="/projects/new" className="btn btn-primary">
              <Plus size={18} />
              Create Project
            </Link>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 20,
          }}
        >
          {filteredProjects.map((project, index) => {
            const images = projectImageUrls[project.id] || 
              (project.coverImageUrl ? [project.coverImageUrl] : []);
            const statusStyle = STATUS_COLORS[project.status || "active"] || STATUS_COLORS.active;
            
            return (
            <div
              key={project.id}
              style={{
                position: "relative",
                borderRadius: 14,
                overflow: "hidden",
                background: "var(--bg-primary)",
                border: "1px solid var(--border-color)",
                transition: "transform 0.35s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(-3px) scale(1.008)";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 12px 40px oklch(0.1 0.02 250 / 0.12), 0 4px 12px oklch(0.1 0 0 / 0.06)";
                // Show delete button
                const del = e.currentTarget.querySelector<HTMLElement>("[data-delete-btn]");
                if (del) del.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(0) scale(1)";
                (e.currentTarget as HTMLElement).style.boxShadow = "none";
                const del = e.currentTarget.querySelector<HTMLElement>("[data-delete-btn]");
                if (del) del.style.opacity = "0";
              }}
              {...(index === 0 ? { 'data-tour-id': 'projects-first-card' } : {})}
            >
              <Link
                to={`/projects/${project.id}`}
                style={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                {/* ── IMAGE CAROUSEL HERO ── */}
                <div {...(index === 0 ? { 'data-tour-id': 'projects-image-carousel' } : {})}>
                <ProjectImageCarousel
                  images={images}
                  projectName={project.job_location}
                  statusColor={statusStyle.color}
                  aspectRatio="16/10"
                  autoAdvanceMs={5000}
                  showDots={true}
                  showArrows={true}
                  showCount={true}
                  borderRadius="0"
                />
                </div>

                {/* ── CONTENT AREA ── */}
                <div style={{ padding: "14px 16px 16px" }}>
                  {/* Title row: name + status */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <h4
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        margin: 0,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {project.job_location}
                    </h4>
                    {getStatusBadge(project.status)}
                  </div>

                  {/* Address */}
                  <p
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.78rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      margin: "0 0 12px 0",
                    }}
                  >
                    {project.job_address}
                  </p>

                  {/* ── Financial summary row ── */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                      marginBottom: 10,
                    }}
                  >
                    {/* Contract value chip */}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "var(--bg-tertiary)",
                        borderRadius: 6,
                        padding: "3px 8px",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: "var(--text-primary)",
                      }}
                    >
                      <DollarSign size={11} style={{ color: "oklch(0.55 0.12 155)", flexShrink: 0 }} />
                      {formatCurrency(
                        (project.contractValue || 0) +
                        (project.approvedVariationsTotal || 0),
                      )}
                    </span>

                    {/* Variations count */}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        background: "var(--bg-tertiary)",
                        borderRadius: 6,
                        padding: "3px 8px",
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        color: "var(--text-secondary)",
                      }}
                      {...(index === 0 ? { 'data-tour-id': 'projects-variations-col' } : {})}
                    >
                      {project.variationsCount || 0} Vars
                    </span>

                    {/* Defect ratio chip */}
                    {(project.defectRatio || 0) > 0 && (
                      <span
                        data-seq-private=""
                        title={`Defect % = Patching ÷ Construction Income × 100\n\nPatching: ${formatCurrency(project.pnlPatching || 0)}\nConstruction Income: ${formatCurrency(project.pnlConstructionIncome || 0)}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          background: (project.defectRatio || 0) > 5 
                            ? "oklch(0.50 0.15 25 / 0.08)"
                            : "var(--bg-tertiary)",
                          borderRadius: 6,
                          padding: "3px 8px",
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: (project.defectRatio || 0) > 5 
                            ? "oklch(0.55 0.18 25)"
                            : "var(--text-secondary)",
                          cursor: "help",
                        }}
                      >
                        {project.defectRatio?.toFixed(1)}% Defect
                      </span>
                    )}
                  </div>

                  {/* ── Bottom row: badges ── */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Progress mini-bar */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 60,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 4,
                          borderRadius: 2,
                          background: "var(--bg-tertiary)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(
                              ((project.progressTotal || 0) /
                                Math.max(project.contractValue || 1, 1)) *
                                100,
                              100
                            )}%`,
                            height: "100%",
                            borderRadius: 2,
                            background: "oklch(0.58 0.14 250)",
                            transition: "width 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {Math.round(
                          ((project.progressTotal || 0) /
                            Math.max(project.contractValue || 1, 1)) *
                            100
                        )}%
                      </span>
                    </div>

                    {/* Claim due indicator */}
                    {(project.status || "active") === "active" && (
                      <span
                        {...(index === 0 ? { 'data-tour-id': 'projects-claim-due' } : {})}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: "0.68rem",
                          fontWeight: 500,
                          color: project.claimDueStatus === "red"
                            ? "oklch(0.55 0.2 25)"
                            : project.claimDueStatus === "orange"
                              ? "oklch(0.60 0.15 75)"
                              : "var(--text-secondary)",
                        }}
                      >
                        <span
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            flexShrink: 0,
                            background:
                              project.claimDueStatus === "red"
                                ? "oklch(0.55 0.2 25)"
                                : project.claimDueStatus === "orange"
                                  ? "oklch(0.65 0.16 75)"
                                  : "oklch(0.58 0.14 155)",
                          }}
                        />
                        {project.currentMonthClaimed && (
                          <span style={{ color: "oklch(0.55 0.15 155)", fontWeight: 700 }}>✓</span>
                        )}
                        {project.claimDueLabel || "—"}
                      </span>
                    )}

                    {/* Reconciliation badge */}
                    {(() => {
                      const total = project.reconTotalReports || 0;
                      const unreconciled = project.reconUnreconciledCount || 0;
                      if (total === 0) return null;
                      if (unreconciled === 0) {
                        return (
                          <span
                            title={`All ${total} reports reconciled`}
                            style={{
                              fontSize: "0.65rem",
                              color: "oklch(0.50 0.14 155)",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                              cursor: "help",
                            }}
                          >
                            <ShieldCheck size={10} />
                          </span>
                        );
                      }
                      return (
                        <span
                          title={`${unreconciled} of ${total} reports unreconciled`}
                          style={{
                            fontSize: "0.65rem",
                            color: unreconciled >= 3 ? "oklch(0.55 0.18 25)" : "oklch(0.6 0.14 75)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            cursor: "help",
                          }}
                        >
                          <AlertTriangle size={10} />
                          {unreconciled}
                        </span>
                      );
                    })()}

                    {/* Dashpivot sync badge */}
                    {project.last_dashpivot_sync_at && (
                      <span
                        style={{
                          fontSize: "0.62rem",
                          color: "var(--text-muted)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          opacity: 0.7,
                        }}
                      >
                        <RefreshCw size={8} />
                      </span>
                    )}
                  </div>
                </div>
              </Link>

              {/* Delete Button: top right over image. Org admins only, the same
                  rule as the claims_projects DELETE policy. */}
              {isAdmin && (
              <button
                data-delete-btn
                className="btn btn-ghost btn-sm"
                title="Delete project"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteConfirm(project.id);
                }}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 5,
                  color: "oklch(0.95 0 0)",
                  opacity: 0,
                  transition: "opacity 0.2s, color 0.2s, background 0.2s",
                  padding: 6,
                  background: "oklch(0.15 0 0 / 0.5)",
                  backdropFilter: "blur(4px)",
                  borderRadius: 6,
                  border: "none",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "oklch(0.50 0.18 25 / 0.85)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "oklch(0.15 0 0 / 0.5)";
                }}
              >
                <Trash2 size={13} />
              </button>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal-animated" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Project</h3>
            </div>
            <div className="modal-body">
              <p style={{ color: "var(--text-secondary)" }}>
                Are you sure you want to delete this project? This action cannot
                be undone and all associated claims data will be permanently
                removed.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleDelete(deleteConfirm)}
                style={{ background: "var(--color-error)" }}
              >
                <Trash2 size={16} />
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Onboarding Tour */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}
    </div>
  );
}
